import styled, { css } from 'styled-components'

export const SearchContainer = styled.div<{ $expanded: boolean }>`
  position: relative;
  display: flex;
  align-items: center;

  /* Desktop: Always show full search */
  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 240px;
  }

  /* Mobile: Collapsible search */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${({ $expanded }) =>
      $expanded
        ? css`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: ${({ theme }) => theme.colors.background.primary};
            z-index: 1000;
            padding: ${({ theme }) => theme.spacing.default};
            padding-top: env(
              safe-area-inset-top,
              ${({ theme }) => theme.spacing.default}
            );
          `
        : css`
            width: auto;
          `}
  }
`

export const SearchButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.touchTargets.minimum};
  height: ${({ theme }) => theme.touchTargets.minimum};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  /* Mobile: Show button when collapsed */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }

  /* Touch devices: Disable hover effects */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.secondary};
      background: transparent;
    }

    &:active {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const InputWrapper = styled.div<{ $expanded: boolean }>`
  position: relative;
  width: 100%;

  /* Desktop: Always visible */
  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }

  /* Mobile: Controlled by expanded state */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $expanded }) => ($expanded ? 'flex' : 'none')};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.default};
  }
`

export const SearchInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
`

export const SearchInput = styled.input`
  flex: 1;
  height: ${({ theme }) => theme.touchTargets.comfortable};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  padding-left: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px; /* Prevent iOS zoom */
  transition: all ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  /* Mobile: Full width with larger touch target */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.touchTargets.comfortable};
  }
`

export const SearchIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`

export const CloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.touchTargets.minimum};
  min-height: ${({ theme }) => theme.touchTargets.minimum};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  /* Mobile: Show close button when expanded */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }

  /* Touch devices: Disable hover effects */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.secondary};
      background: transparent;
    }

    &:active {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const ClearButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  svg {
    width: 13px;
    height: 13px;
  }

  /* Mobile: Ensure touch target */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
  }

  /* Touch devices: Disable hover effects */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.tertiary};
      background: transparent;
    }

    &:active {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.tertiary};
    }
  }
`
